/**
 * Demo Trigger Service
 * Handles demo initialization logic, completion tracking, and localStorage management
 * Determines when to show the demo to users
 */

import { DEMO_BEHAVIOR } from '@/demo/constants';

/**
 * Storage keys for demo state
 */
const STORAGE_KEYS = {
  COMPLETED: DEMO_BEHAVIOR.STORAGE_KEY_COMPLETED || 'north_star_demo_completed',
  PROGRESS: DEMO_BEHAVIOR.STORAGE_KEY_PROGRESS || 'north_star_demo_progress',
  INTERACTIONS: DEMO_BEHAVIOR.STORAGE_KEY_INTERACTIONS || 'north_star_demo_interactions',
  TIMESTAMP: 'north_star_demo_timestamp',
  VERSION: 'north_star_demo_version',
};

/**
 * Demo version - increment when demo steps change significantly
 */
const DEMO_VERSION = '1.0.0';

/**
 * Check if browser supports localStorage
 * @returns {boolean}
 */
function isLocalStorageAvailable() {
  try {
    const testKey = '__demo_localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('[DemoTriggerService] localStorage not available:', error);
    return false;
  }
}

/**
 * Get completion status from localStorage
 * @returns {boolean} - True if demo was completed
 */
export function isDemoCompleted() {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const completed = localStorage.getItem(STORAGE_KEYS.COMPLETED);
    const version = localStorage.getItem(STORAGE_KEYS.VERSION);

    // If demo version changed, reset completion status
    if (version && version !== DEMO_VERSION) {
      clearDemoState();
      return false;
    }

    return completed === 'true';
  } catch (error) {
    console.warn('[DemoTriggerService] Error checking completion:', error);
    return false;
  }
}

/**
 * Get demo progress from localStorage
 * @returns {Object} - { currentStep, completedSteps, timestamp }
 */
export function getDemoProgress() {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const progress = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    if (!progress) {
      return null;
    }

    return JSON.parse(progress);
  } catch (error) {
    console.warn('[DemoTriggerService] Error retrieving progress:', error);
    return null;
  }
}

/**
 * Save demo progress to localStorage
 * @param {Object} progressData - { currentStep, completedSteps }
 */
export function saveDemoProgress(progressData) {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const progress = {
      ...progressData,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(progress));
  } catch (error) {
    console.warn('[DemoTriggerService] Error saving progress:', error);
  }
}

/**
 * Get demo interactions from localStorage
 * @returns {Object|null} - User interactions object or null
 */
export function getDemoInteractions() {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const interactions = localStorage.getItem(STORAGE_KEYS.INTERACTIONS);
    if (!interactions) {
      return null;
    }

    return JSON.parse(interactions);
  } catch (error) {
    console.warn('[DemoTriggerService] Error retrieving interactions:', error);
    return null;
  }
}

/**
 * Save demo interactions to localStorage
 * @param {Object} interactions - User interactions object
 */
export function saveDemoInteractions(interactions) {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEYS.INTERACTIONS, JSON.stringify(interactions));
  } catch (error) {
    console.warn('[DemoTriggerService] Error saving interactions:', error);
  }
}

/**
 * Mark demo as completed
 * Saves completion status and timestamp to localStorage
 */
export function markDemoAsCompleted() {
  if (!isLocalStorageAvailable()) {
    console.warn('[DemoTriggerService] Cannot mark demo as completed - localStorage unavailable');
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEYS.COMPLETED, 'true');
    localStorage.setItem(STORAGE_KEYS.TIMESTAMP, new Date().toISOString());
    localStorage.setItem(STORAGE_KEYS.VERSION, DEMO_VERSION);
  } catch (error) {
    console.warn('[DemoTriggerService] Error marking demo as completed:', error);
  }
}

/**
 * Check if user is first-time (no demo completion record)
 * @returns {boolean} - True if first-time user
 */
export function isFirstTimeUser() {
  return !isDemoCompleted();
}

/**
 * Determine if demo should be shown
 * @param {Object} options - Configuration options
 * @returns {boolean}
 */
export function shouldShowDemo(options = {}) {
  const {
    forceShow = false,
    checkFirstTime = true,
    checkCompletion = true,
    checkVersion = true,
  } = options;

  // Force show overrides all checks
  if (forceShow) {
    return true;
  }

  // Check if already completed
  if (checkCompletion && isDemoCompleted()) {
    return false;
  }

  // Check if first-time user
  if (checkFirstTime && !isFirstTimeUser()) {
    return false;
  }

  // Check version compatibility
  if (checkVersion && isLocalStorageAvailable()) {
    const version = localStorage.getItem(STORAGE_KEYS.VERSION);
    if (version && version !== DEMO_VERSION) {
      // Version mismatch - reset and show demo
      clearDemoState();
      return true;
    }
  }

  return true;
}

/**
 * Get demo completion timestamp
 * @returns {Date|null} - Completion timestamp or null
 */
export function getDemoCompletionTimestamp() {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
    if (!timestamp) {
      return null;
    }

    return new Date(timestamp);
  } catch (error) {
    console.warn('[DemoTriggerService] Error getting completion timestamp:', error);
    return null;
  }
}

/**
 * Get time since demo completion (in milliseconds)
 * @returns {number|null} - Time elapsed or null if not completed
 */
export function getTimeSinceCompletion() {
  const timestamp = getDemoCompletionTimestamp();
  if (!timestamp) {
    return null;
  }

  return Date.now() - timestamp.getTime();
}

/**
 * Check if demo should be re-shown based on time elapsed
 * Useful for showing demo again after some time has passed
 * @param {number} daysThreshold - Days since completion to re-show (default: 30)
 * @returns {boolean}
 */
export function shouldReShowDemo(daysThreshold = 30) {
  const elapsed = getTimeSinceCompletion();
  if (elapsed === null) {
    return true; // Never completed, should show
  }

  const daysSinceCompletion = elapsed / (1000 * 60 * 60 * 24);
  return daysSinceCompletion >= daysThreshold;
}

/**
 * Clear all demo state from localStorage
 * Useful for testing or resetting demo
 */
export function clearDemoState() {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('[DemoTriggerService] Error clearing demo state:', error);
  }
}

/**
 * Reset demo (clear progress but not completion)
 * Used when user wants to restart fresh
 */
export function resetDemoProgress() {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.INTERACTIONS);
  } catch (error) {
    console.warn('[DemoTriggerService] Error resetting demo progress:', error);
  }
}

/**
 * Get demo statistics
 * @returns {Object} - { completed, firstTimeUser, timeSinceCompletion, demoVersion, ... }
 */
export function getDemoStatistics() {
  return {
    completed: isDemoCompleted(),
    firstTimeUser: isFirstTimeUser(),
    completionTimestamp: getDemoCompletionTimestamp(),
    timeSinceCompletion: getTimeSinceCompletion(),
    progress: getDemoProgress(),
    interactions: getDemoInteractions(),
    demoVersion: DEMO_VERSION,
    localStorageAvailable: isLocalStorageAvailable(),
  };
}

/**
 * Initialize demo trigger service
 * Call this on app initialization to setup demo logic
 * @param {Object} config - Configuration options
 * @returns {Object} - Service status
 */
export function initializeDemoService(config = {}) {
  const {
    version = DEMO_VERSION,
    storageKeyPrefix = 'north_star',
  } = config;

  // Update version if provided
  if (version !== DEMO_VERSION) {
    console.info('[DemoTriggerService] Demo version mismatch. Resetting...');
    clearDemoState();
  }

  return {
    initialized: true,
    version,
    storageAvailable: isLocalStorageAvailable(),
    demoCompleted: isDemoCompleted(),
    stats: getDemoStatistics(),
  };
}

/**
 * Export all functions
 */
export default {
  isDemoCompleted,
  getDemoProgress,
  saveDemoProgress,
  getDemoInteractions,
  saveDemoInteractions,
  markDemoAsCompleted,
  isFirstTimeUser,
  shouldShowDemo,
  getDemoCompletionTimestamp,
  getTimeSinceCompletion,
  shouldReShowDemo,
  clearDemoState,
  resetDemoProgress,
  getDemoStatistics,
  initializeDemoService,
};
